import { useState, useMemo, useCallback, useEffect } from "react";
import AdminLayout from "@/pages/admin/components/AdminLayout";
import CategoryTree from "./components/CategoryTree";
import DataTable from "./components/DataTable";
import CopyToSiteModal from "./components/CopyToSiteModal";
import { cloudListings, cloudCategories, CloudListing } from "@/mocks/cloudData";

const PAGE_SIZE = 20;

export default function CloudDataPage() {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [catSearch, setCatSearch] = useState("");
  const [keyword, setKeyword] = useState("");
  const [inputKeyword, setInputKeyword] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);
  const [loading, setLoading] = useState(false);

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Simulate loading when filter changes
  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => setLoading(false), 300);
    return () => clearTimeout(t);
  }, [selectedCategoryId, keyword, currentPage]);

  const filteredData = useMemo(() => {
    let result = cloudListings;
    if (selectedCategoryId) {
      const cat = cloudCategories.find((c) => c.id === selectedCategoryId);
      if (cat?.parentId === null) {
        // parent category: include all children
        const childIds = cloudCategories
          .filter((c) => c.parentId === selectedCategoryId)
          .map((c) => c.id);
        result = result.filter((d) => childIds.includes(d.categoryId) || d.categoryId === selectedCategoryId);
      } else {
        result = result.filter((d) => d.categoryId === selectedCategoryId);
      }
    }
    if (keyword.trim()) {
      const kw = keyword.toLowerCase();
      result = result.filter(
        (d) =>
          d.title.toLowerCase().includes(kw) ||
          d.companyName.toLowerCase().includes(kw) ||
          d.categoryName.toLowerCase().includes(kw) ||
          d.summary.toLowerCase().includes(kw)
      );
    }
    return result;
  }, [selectedCategoryId, keyword]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredData.slice(start, start + PAGE_SIZE);
  }, [filteredData, currentPage]);

  const handleCategorySelect = useCallback((id: string | null) => {
    setSelectedCategoryId(id);
    setCurrentPage(1);
    setSelectedIds(new Set());
  }, []);

  const handleSearch = () => {
    setKeyword(inputKeyword);
    setCurrentPage(1);
    setSelectedIds(new Set());
  };

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleToggleSelectAll = useCallback((ids: string[]) => {
    setSelectedIds((prev) => {
      const allSelected = ids.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
  }, []);

  const handleSelectAllFiltered = () => {
    const allIds = new Set(filteredData.map((d) => d.id));
    setSelectedIds(allIds);
  };

  const handleClearSelection = () => setSelectedIds(new Set());

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const selectedItems = useMemo(
    () => cloudListings.filter((d) => selectedIds.has(d.id)),
    [selectedIds]
  );

  const handleCopyConfirm = (targetCategoryId: string, targetCategoryName: string) => {
    setShowCopyModal(false);
    showToast(`已成功将 ${selectedIds.size} 条数据复制到「${targetCategoryName}」分类，待审核后发布`);
    setSelectedIds(new Set());
  };

  const currentCategoryName = useMemo(() => {
    if (!selectedCategoryId) return "全部分类";
    return cloudCategories.find((c) => c.id === selectedCategoryId)?.name || "全部分类";
  }, [selectedCategoryId]);

  return (
    <AdminLayout>
      <div className="flex flex-col h-[calc(100vh-3.5rem-3rem)] -m-6">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-semibold text-gray-800">云端数据</h1>
              <p className="text-xs text-gray-400 mt-0.5">从云端数据库批量选择数据，复制到网站分类中</p>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-cloud-line text-orange-400" />
              </div>
              <span>云端数据库</span>
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
              <span className="text-green-500">已连接</span>
            </div>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left: Category Tree */}
          <aside className="w-56 bg-white border-r border-gray-200 flex flex-col shrink-0">
            <div className="px-3 pt-3 pb-2 shrink-0">
              <div className="relative">
                <div className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-search-line text-gray-400 text-xs" />
                </div>
                <input
                  type="text"
                  placeholder="搜索分类..."
                  value={catSearch}
                  onChange={(e) => setCatSearch(e.target.value)}
                  className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 bg-gray-50"
                />
              </div>
            </div>
            <div className="flex-1 overflow-hidden">
              <CategoryTree
                selectedCategoryId={selectedCategoryId}
                onSelect={handleCategorySelect}
                searchKeyword={catSearch}
              />
            </div>
          </aside>

          {/* Right: Data Area */}
          <div className="flex-1 flex flex-col overflow-hidden bg-white">
            {/* Toolbar */}
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3 shrink-0">
              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-folder-open-line text-orange-400" />
                </div>
                <span className="font-medium">{currentCategoryName}</span>
                <span className="text-gray-400">({filteredData.length.toLocaleString()} 条)</span>
              </div>

              <div className="flex-1" />

              {/* Search */}
              <div className="flex items-center gap-2">
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                    <i className="ri-search-line text-gray-400 text-sm" />
                  </div>
                  <input
                    type="text"
                    placeholder="搜索标题、公司名..."
                    value={inputKeyword}
                    onChange={(e) => setInputKeyword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    className="pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 w-52 bg-gray-50"
                  />
                </div>
                <button
                  onClick={handleSearch}
                  className="px-3 py-1.5 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  搜索
                </button>
                {keyword && (
                  <button
                    onClick={() => { setKeyword(""); setInputKeyword(""); setCurrentPage(1); }}
                    className="px-3 py-1.5 text-sm text-gray-400 hover:text-gray-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    清除
                  </button>
                )}
              </div>
            </div>

            {/* Batch Action Bar */}
            {selectedIds.size > 0 && (
              <div className="px-4 py-2.5 bg-orange-50 border-b border-orange-100 flex items-center gap-3 shrink-0">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-checkbox-circle-line text-orange-500" />
                </div>
                <span className="text-sm text-orange-700">
                  已选 <span className="font-semibold">{selectedIds.size}</span> 条
                </span>
                <button
                  onClick={handleSelectAllFiltered}
                  className="text-xs text-orange-500 hover:text-orange-700 underline cursor-pointer whitespace-nowrap"
                >
                  选择全部 {filteredData.length.toLocaleString()} 条
                </button>
                <button
                  onClick={handleClearSelection}
                  className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer whitespace-nowrap"
                >
                  取消选择
                </button>
                <div className="flex-1" />
                <button
                  onClick={() => setShowCopyModal(true)}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap font-medium"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-file-copy-line text-sm" />
                  </div>
                  复制到网站分类
                </button>
              </div>
            )}

            {/* Table */}
            <div className="flex-1 overflow-hidden">
              <DataTable
                data={paginatedData}
                selectedIds={selectedIds}
                onToggleSelect={handleToggleSelect}
                onToggleSelectAll={handleToggleSelectAll}
                currentPage={currentPage}
                pageSize={PAGE_SIZE}
                totalCount={filteredData.length}
                onPageChange={handlePageChange}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Copy Modal */}
      {showCopyModal && (
        <CopyToSiteModal
          selectedItems={selectedItems}
          onClose={() => setShowCopyModal(false)}
          onConfirm={handleCopyConfirm}
        />
      )}

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium shadow-lg transition-all ${
            toast.type === "success"
              ? "bg-gray-900 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={toast.type === "success" ? "ri-check-line" : "ri-error-warning-line"} />
          </div>
          {toast.msg}
        </div>
      )}
    </AdminLayout>
  );
}
